import { ConfigPaths } from "@omni/types/utils/configPaths";

import { has } from "./object.utils";

export enum MaybeType {
    Just = "maybe-type__just",
    Nothing = "maybe-type__nothing"
}

export interface IJust<T> {
    type: typeof MaybeType.Just;
    value: T;
}

export interface INothing {
    type: typeof MaybeType.Nothing;
}

export type Maybe<T> = IJust<T> | INothing;

export const Nothing = (): INothing => ({
    type: MaybeType.Nothing
});

export const Just = <T>(value: T): IJust<T> => ({
    type: MaybeType.Just,
    value
});

export const isJust = <T>(maybe: Maybe<T>): maybe is IJust<T> =>
    maybe.type === MaybeType.Just;

export const isNothing = <T>(maybe: Maybe<T>): maybe is INothing =>
    maybe.type === MaybeType.Nothing;

export const createMayBeFor = <T>(
    requiredProps: Array<ConfigPaths<T>>,
    data?: any | null
) => {
    if (data === undefined || data === null) {
        return Nothing();
    }

    try {
        return requiredProps.every((p) => has(data, p))
            ? Just<T>(data)
            : Nothing();
    } catch {
        return Nothing();
    }
};

export const createMayBeForOptional = <T>(
    optionalProps: Array<ConfigPaths<T>>,
    data?: any
) => {
    if (!data) {
        return Nothing();
    }
    try {
        return optionalProps.some((p) => has(data, p))
            ? Just<Partial<T>>(data)
            : Nothing();
    } catch {
        return Nothing();
    }
};

export const of = <T>(data?: T) => createMayBeFor<T>([], data);

export const get = <T>(just: IJust<T>): T => just.value;

export const getOrElse =
    <T>(maybe: Maybe<T>) =>
    <U>(otherValue: U): T | U =>
        isJust(maybe) ? get(maybe) : otherValue;

export const map =
    <T>(maybe: Maybe<T>) =>
    <U>(mapper: (value: T) => U) => {
        if (isJust(maybe)) {
            const value = mapper(maybe.value);

            return of(value);
        } else {
            return Nothing();
        }
    };

export const mapAsync =
    <T>(maybe: Maybe<T>) =>
    async <U>(mapper: (value: T) => Promise<U>) =>
        isJust(maybe) ? Just(await mapper(maybe.value)) : Nothing();

export const flatMap =
    <T>(maybe: Maybe<T>) =>
    <U>(mapper: (value: T) => Maybe<U>) =>
        isJust(maybe) ? mapper(maybe.value) : Nothing();

export const flatMapAsync =
    <T>(maybe: Maybe<T>) =>
    async <U>(mapper: (value: T) => Promise<Maybe<U>>) =>
        isJust(maybe) ? mapper(maybe.value) : Nothing();

export const ifJust =
    <T>(maybe: Maybe<T>) =>
    (consumer: (value: T) => void) =>
        isJust(maybe) ? consumer(get(maybe)) : undefined;

export const match =
    <T>(maybe: Maybe<T>) =>
    (just: (value: T) => void, nothing: () => void) =>
        isJust(maybe) ? just(maybe.value) : nothing();

export const toArray = <T>(maybe: Maybe<T>) =>
    isJust(maybe) ? [get(maybe)] : [];
